import { RequestStatusEnum } from "@/lib/enum/request-status-enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IsFormable } from "@/lib/formable";
import { DocumentRequestGoToHelper } from "@/lib/utility/document-request.go-to-helper";
import documentRequestService from "@/services/mrfiktiv/services/document-request.service";
import documentRequestResponseService from "@/services/mrfiktiv/services/document-request-response.service";
import {
  MrfiktivDocumentRequestPopulatedViewModelGen,
  MrfiktivDocumentRequestViewModelGen,
  MrfiktivDocumentViewModelGen,
  MrfiktivStatusHistoryElementViewModelGen,
  MrfiktivUpdateDocumentRequestDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { DocumentRequestDataAccessLayer } from "@/store/modules/access-layers/document-request.access-layer";
import { PartnerUserAccessLayer } from "@/store/modules/access-layers/partner-user.access-layer";
import VueRouter from "vue-router";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { ActivityLog } from "./activity-log.entity";
import { ClosedByAt, IClosedByAt } from "./by-at.entity";
import { IReference, Reference } from "./reference.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { IPartnerUser, PartnerUser } from "./user.entity";
import { $t } from "@/lib/utility/t";

@IsFormable
@IsFilterable
export class DocumentRequestBase implements MrfiktivDocumentRequestPopulatedViewModelGen {
  readonly resourceType = BackendResourceEnum.DOCUMENT_REQUEST;


  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "id"
  })
  id!: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.documentRequest.partnerId"
  })
  partnerId!: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.documentRequest.userId"
  })
  userId?: string;

  timestamp!: ITimestamp;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.documentRequest.title"
  })
  title?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.documentRequest.description"
  })
  description?: string;

  closed?: IClosedByAt;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.documentRequest.status",
    config: { items: Object.values(RequestStatusEnum) }
  })
  status!: RequestStatusEnum;

  history: MrfiktivStatusHistoryElementViewModelGen[] = [];

  documentIds?: string[];

  documents?: MrfiktivDocumentViewModelGen[];

  assignees?: string[];

  refs: IReference[] = [];

  get refsCategories(): BackendResourceEnum[] {
    return [];
  }

  get user(): IPartnerUser | undefined {
    if (!this.userId) {
      return undefined;
    }
    return PartnerUserAccessLayer.get(this.userId);
  }

  static get statusColorMap() {
    return new Map([
      [RequestStatusEnum.DRAFT, "grey"],
      [RequestStatusEnum.REQUESTED, "primary"],
      [RequestStatusEnum.REPLIED, "green lighten-2"],
      [RequestStatusEnum.ACCEPTED, "green"],
      [RequestStatusEnum.REJECTED_BY_REQUESTED, "red"],
      [RequestStatusEnum.REJECTED_BY_REQUESTING, "red"],
      [RequestStatusEnum.REQUEST_MORE_DATA, "orange"],
      [RequestStatusEnum.FINISHED, "deep-purple lighten-1"]
    ]);
  }

  static get statusIconMap() {
    return new Map([
      [RequestStatusEnum.DRAFT, "mdi-file-document-outline"],
      [RequestStatusEnum.REQUESTED, "mdi-file-send-outline"],
      [RequestStatusEnum.REPLIED, "mdi-file-document-outline"],
      [RequestStatusEnum.REQUEST_MORE_DATA, "mdi-message-question-outline"],
      [RequestStatusEnum.ACCEPTED, "mdi-check-circle-outline"],
      [RequestStatusEnum.REJECTED_BY_REQUESTED, "mdi-close-circle-outline"],
      [RequestStatusEnum.REJECTED_BY_REQUESTING, "mdi-close-circle-outline"],
      [RequestStatusEnum.FINISHED, "mdi-check-bold"]
    ]);
  }

  get statusColor() {
    return DocumentRequestBase.statusColorMap.get(this.status as RequestStatusEnum);
  }

  get statusIcon() {
    return DocumentRequestBase.statusIconMap.get(this.status as RequestStatusEnum);
  }

  get statusReadable() {
    return $t(`enums.RequestStatusEnum.${this.status}`);
  }

  get isRepliedOrFinal(): boolean {
    if (this.status === RequestStatusEnum.REPLIED) {
      return true;
    }
    return this.isFinal;
  }


  get isFinal(): boolean {
    const finals = [
      RequestStatusEnum.ACCEPTED,
      RequestStatusEnum.REJECTED_BY_REQUESTED,
      RequestStatusEnum.REJECTED_BY_REQUESTING,
      RequestStatusEnum.FINISHED
    ];
    return finals.includes(this.status as RequestStatusEnum);
  }

  constructor(data?: Partial<MrfiktivDocumentRequestViewModelGen | MrfiktivDocumentRequestPopulatedViewModelGen>) {
    if (!data) {
      return;
    }
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.userId = data.userId;
    this.timestamp = new Timestamp(data.timestamp);
    this.title = data.title;
    this.description = data.description ?? undefined;
    this.closed = data.closed ? new ClosedByAt(data.closed) : undefined;
    this.status = (data.status ?? RequestStatusEnum.DRAFT) as RequestStatusEnum;
    this.history = data.history ?? [];
    this.documentIds = data.documentIds;
    this.assignees = (data as MrfiktivDocumentRequestPopulatedViewModelGen).assignees;
    this.refs = Reference.filterDuplicates(((data as any).refs ?? []).map((ref: IReference) => new Reference(ref)));
    this.mapPopulated(data as MrfiktivDocumentRequestPopulatedViewModelGen);
  }

  map(data: MrfiktivDocumentRequestViewModelGen | MrfiktivDocumentRequestPopulatedViewModelGen): void {
    this.id = data.id;
    this.partnerId = data.partnerId;
    this.userId = data.userId;
    this.timestamp = new Timestamp(data.timestamp);
    this.title = data.title;
    this.description = data.description ?? undefined;
    this.closed = data.closed ? new ClosedByAt(data.closed) : undefined;
    this.status = (data.status ?? RequestStatusEnum.DRAFT) as RequestStatusEnum;
    this.history = data.history ?? [];
    this.documentIds = data.documentIds;
    this.assignees = (data as MrfiktivDocumentRequestPopulatedViewModelGen).assignees;
    this.refs = Reference.filterDuplicates(((data as any).refs ?? []).map((ref: IReference) => new Reference(ref)));
    this.mapPopulated(data as MrfiktivDocumentRequestPopulatedViewModelGen);
  }

  mapPopulated(populatedData: MrfiktivDocumentRequestPopulatedViewModelGen): void {
    const user = populatedData.user;
    if (user) {
      PartnerUserAccessLayer.set(new PartnerUser(user));
    }
    this.documents = populatedData.documents ?? [];
  }

  goTo(router: VueRouter) {
    return {
      detail: () =>
        new DocumentRequestGoToHelper(router).goToDocumentRequestDetail({
          documentRequestId: this.id,
          partnerId: this.partnerId
        }),
      table: () =>
        new DocumentRequestGoToHelper(router).goToDocumentRequestTable({
          partnerId: this.partnerId
        })
    };
  }

  async createAssigneeActivity(
    activityType: ActivityTypeEnum.CREATE_ASSIGNEE | ActivityTypeEnum.DELETE_ASSIGNEE,
    newAssignees?: string[]
  ) {
    if (!newAssignees?.length) return;
    await new ActivityLog().createAssigneeActivity({
      partnerId: this.partnerId,
      source: { refType: ResourceEnum.DOCUMENT_REQUEST, refId: this.id },
      newAssignees,
      activityType
    });
  }

  async delete() {
    return documentRequestService.delete(this.partnerId, this.id);
  }

  async updatePartial(dto: MrfiktivUpdateDocumentRequestDtoGen & { refs?: IReference[] }) {
    const updated = await documentRequestService.update(this.partnerId, this.id, dto as MrfiktivUpdateDocumentRequestDtoGen);
    this.map(updated);
    DocumentRequestDataAccessLayer.set(this);
    return this;
  }

  async create() {
    const created = await documentRequestService.create(this.partnerId, {
      title: this.title,
      description: this.description ?? null,
      status: this.status,
      assignees: this.assignees ?? []
    });
    this.map(created);
    DocumentRequestDataAccessLayer.set(this);
    return this;
  }

  async fetch() {
    const res = await documentRequestService.getOne(this.partnerId, this.id);
    this.map(res);
    DocumentRequestDataAccessLayer.set(this);
    return this;
  }

  async respond(sct: string, message?: string, files?: File[], status = "REPLIED") {
    const updated = await documentRequestResponseService.respond(
      { partnerId: this.partnerId, documentRequestId: this.id, sct },
      { status: status as "REPLIED", message, files }
    );
    this.map(updated);
    DocumentRequestDataAccessLayer.set(this);
    return this;
  }
}

type IDocumentRequest = DocumentRequestBase;
const DocumentRequest = Filter.createForClass(DocumentRequestBase);

export { IDocumentRequest, DocumentRequest };
